import {
  cancelDownloadJob as persistDownloadJobCancellation,
  createDownloadJob as persistDownloadJob,
  getDownloadJob as readDownloadJob,
  listIncompleteDownloadJobs,
  normalizeIncompleteDownloadJobs,
  updateDownloadJob as persistDownloadJobUpdate,
} from "./downloadPersistence";
import { createDownloadJobSession } from "./downloadWorker";

function assertManagerDatabase(db) {
  if (!db) {
    throw new Error("A download persistence database is required.");
  }
}

function assertJobId(jobId) {
  if (!jobId) {
    throw new Error("A download job id is required.");
  }
}

function createSessionRecord({ jobId, session, runPromise }) {
  return {
    jobId,
    session,
    runPromise,
  };
}

function shouldPauseStartupJob(job) {
  return Boolean(job && ["queued", "running"].includes(job.status));
}

function normalizeStartupJob({ job, nowMs }) {
  if (!shouldPauseStartupJob(job)) {
    return job;
  }

  return {
    ...job,
    status: "paused",
    updatedAt: new Date(nowMs).toISOString(),
  };
}

export function createDownloadManager({
  db,
  sessionFactory = createDownloadJobSession,
  incompleteJobsLoader = listIncompleteDownloadJobs,
  incompleteJobsNormalizer = normalizeIncompleteDownloadJobs,
  now = () => Date.now(),
} = {}) {
  assertManagerDatabase(db);

  const activeSessions = new Map();

  function createSession({
    jobId,
    rootDirectoryHandle,
    getSignedUrl,
    fetchImpl,
  }) {
    return sessionFactory({
      db,
      jobId,
      rootDirectoryHandle,
      getSignedUrl,
      ...(fetchImpl ? { fetchImpl } : {}),
    });
  }

  function trackRunPromise({ jobId, record, runPromise }) {
    const trackedPromise = Promise.resolve(runPromise).finally(() => {
      const currentRecord = activeSessions.get(jobId);

      if (currentRecord && currentRecord.runPromise === trackedPromise) {
        activeSessions.delete(jobId);
      }
    });
    record.runPromise = trackedPromise;

    return record.runPromise;
  }

  function storeSession({ jobId, session, runPromise }) {
    const record = createSessionRecord({
      jobId,
      session,
      runPromise: null,
    });

    activeSessions.set(jobId, record);
    trackRunPromise({ jobId, record, runPromise });

    return record;
  }

  function getSessionRecord(jobId) {
    assertJobId(jobId);
    return activeSessions.get(jobId) || null;
  }

  return {
    startDownload({
      jobId,
      rootDirectoryHandle,
      getSignedUrl,
      fetchImpl,
    }) {
      assertJobId(jobId);

      const existingRecord = activeSessions.get(jobId);

      if (existingRecord) {
        return existingRecord;
      }

      const session = createSession({
        jobId,
        rootDirectoryHandle,
        getSignedUrl,
        fetchImpl,
      });
      const runPromise = session.start();

      return storeSession({
        jobId,
        session,
        runPromise,
      });
    },

    pauseDownload({ jobId }) {
      const record = getSessionRecord(jobId);

      if (!record) {
        return null;
      }

      record.session.pause();

      return record;
    },

    resumeDownload({
      jobId,
      rootDirectoryHandle,
      getSignedUrl,
      fetchImpl,
    }) {
      assertJobId(jobId);

      const existingRecord = activeSessions.get(jobId);

      if (existingRecord) {
        if (existingRecord.runPromise) {
          return existingRecord;
        }

        trackRunPromise({
          jobId,
          record: existingRecord,
          runPromise: existingRecord.session.resume(),
        });
        return existingRecord;
      }

      const session = createSession({
        jobId,
        rootDirectoryHandle,
        getSignedUrl,
        fetchImpl,
      });
      const runPromise = session.resume();

      return storeSession({
        jobId,
        session,
        runPromise,
      });
    },

    async cancelDownload({ jobId }) {
      assertJobId(jobId);
      const record = getSessionRecord(jobId);

      if (!record) {
        return persistDownloadJobCancellation({ db, jobId });
      }

      const cancellationPromise =
        record.session && typeof record.session.cancel === "function"
          ? record.session.cancel()
          : record.session.pause();
      const settlePromise = cancellationPromise || record.runPromise;

      if (settlePromise) {
        await settlePromise;
      }

      const currentRecord = activeSessions.get(jobId);

      if (currentRecord === record) {
        activeSessions.delete(jobId);
      }

      const persistedRecord = await readDownloadJob({ db, jobId });

      if (persistedRecord && persistedRecord.job) {
        return persistedRecord.job;
      }

      return persistDownloadJobCancellation({ db, jobId });
    },

    getActiveDownloads() {
      return Array.from(activeSessions.values());
    },

    async listIncompleteDownloads() {
      if (typeof incompleteJobsNormalizer === "function") {
        await incompleteJobsNormalizer({ db });
      }

      const jobs = await incompleteJobsLoader({ db });
      const nowMs = now();

      return Promise.all(
        jobs.map(async (job) => {
          const normalizedJob = normalizeStartupJob({
            job,
            nowMs,
          });

          if (normalizedJob !== job) {
            await persistDownloadJobUpdate({
              db,
              job: normalizedJob,
            });
          }

          return normalizedJob;
        })
      );
    },

    createDownloadJob({ job, files, chunkSizeBytes }) {
      return persistDownloadJob({
        db,
        job,
        files,
        ...(chunkSizeBytes !== undefined ? { chunkSizeBytes } : {}),
      });
    },

    getDownloadJob({ jobId }) {
      assertJobId(jobId);
      return readDownloadJob({ db, jobId });
    },
  };
}
